import { NgModule, isDevMode } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { RouteReuseStrategy, Router } from '@angular/router';
import { IonicModule, IonicRouteStrategy, Platform, ToastController } from '@ionic/angular';
import { ServiceWorkerModule } from '@angular/service-worker';
import {
    ArmyBuilderConfig,
    ArmyBuilderSharedModule,
    ArmyBuilderStoreModule,
    SettingsModule,
    UserService,
    LanguageModule,
    SupportModule,
    pageTransition,
    PurchaseModule,
    DEFAULT_SETTINGS_VALUE,
    SupportService,
    SettingsService,
    GAME_ID,
    EntityPreloadModule,
    TranslationsModule,
    TranslationPreloadModule,
    ContentModule,
    LOAD_LANGUAGE_DATA_FN,
    RestDataService,
    deepMergeObjects,
    RelativeDatePipe,
    PageLayoutModule,
    MenuModule,
    CoOrganiserNotificationHandlerComponent,
    ImpersonationBarComponent,
    BillingSimulationBarComponent,
    SocialSigninModule
} from 'army-builder-shared';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { TranslateModule } from '@ngx-translate/core';
import { NgLetModule } from 'ng-let';
import { KeepAwake } from '@capacitor-community/keep-awake';
import { provideAuth, getAuth, initializeAuth, indexedDBLocalPersistence } from '@angular/fire/auth';
import { provideFirebaseApp, initializeApp, getApp } from '@angular/fire/app';
import { Capacitor } from '@capacitor/core';
import { IonicStorageModule } from '@ionic/storage-angular';
import { debounceTime, filter, firstValueFrom, map, withLatestFrom } from 'rxjs';

import { environment } from '../environments/environment';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { WarlordConfig } from './config';
import { WarlordSettingsModule } from './settings/settings.module';
import { WarlordUserService } from './settings/user/user.service';
import { SubscriptionModule } from './subscription/subscription.module';
import { GAME_IDS } from './game-id.token';

@NgModule({
    declarations: [AppComponent],
    bootstrap: [AppComponent],
    imports: [
        BrowserModule,
        IonicModule.forRoot({
            mode: 'md',
            navAnimation: pageTransition
        }),
        AppRoutingModule,
        MenuModule,
        ArmyBuilderStoreModule,
        ArmyBuilderSharedModule,
        TranslateModule,
        SettingsModule,
        WarlordSettingsModule,
        NgLetModule,
        PageLayoutModule,
        LanguageModule,
        SubscriptionModule,
        NgLetModule,
        SupportModule,
        IonicStorageModule.forRoot(),
        PurchaseModule.forRoot(),
        EntityPreloadModule,
        TranslationPreloadModule,
        ContentModule,
        CoOrganiserNotificationHandlerComponent,
        ImpersonationBarComponent,
        BillingSimulationBarComponent,
        SocialSigninModule,
        RelativeDatePipe,
        MenuModule,
        ServiceWorkerModule.register('ngsw-worker.js', {
            enabled: !isDevMode() && !Capacitor.isNativePlatform(),
            registrationStrategy: 'registerWhenStable:30000'
        })
    ],
    providers: [
        provideFirebaseApp(() => initializeApp(environment.firebase)),
        provideAuth(() => {
            if (Capacitor.isNativePlatform()) {
                return initializeAuth(getApp(), {
                    persistence: indexedDBLocalPersistence
                });
            } else {
                return getAuth();
            }
        }),
        { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
        { provide: ArmyBuilderConfig, useClass: WarlordConfig },
        WarlordUserService,
        {
            provide: UserService,
            useExisting: WarlordUserService
        },
        {
            provide: DEFAULT_SETTINGS_VALUE,
            useValue: {
                // itemFilters: { item: { type: EQUIPMENT_TYPES } },
                // itemSort: {
                //     item: {}
                // },
                // disableGrouping: {
                //     item: {}
                // },
                // showIllegalItems: {
                //     item: {}
                // }
            }
        },
        provideHttpClient(withInterceptorsFromDi()),
        {
            provide: GAME_ID,
            useValue: 'global'
        },
        {
            provide: LOAD_LANGUAGE_DATA_FN,
            deps: [RestDataService, ArmyBuilderConfig],
            useFactory:
                (restDataService: RestDataService, config: ArmyBuilderConfig) =>
                async (gameId: string, lang: string, fallbackToCache: boolean) => {
                    let apiBaseUrl = config.apiBaseUrl;
                    const url = `${apiBaseUrl}/translation/${gameId}/${lang}`;

                    let translations = await firstValueFrom(
                        restDataService.get(url, {
                            cacheKey: `translation_${gameId}_${lang}`,
                            preferCache: false,
                            fallbackToCache
                        })
                    );

                    if (gameId === GAME_IDS.K47) {
                        let base = await firstValueFrom(
                            restDataService.get(`${apiBaseUrl}/translation/${GAME_IDS.BOLT_ACTION_3E}/${lang}`, {
                                cacheKey: `translation_${GAME_IDS.BOLT_ACTION_3E}_${lang}`,
                                preferCache: false,
                                fallbackToCache
                            })
                        );

                        let merged = deepMergeObjects(base.value, translations.value);
                        return merged;
                    }

                    return translations.value;
                }
        }
    ]
})
export class AppModule {
    constructor(
        public platform: Platform,
        private supportService: SupportService,
        private settingsService: SettingsService,
        private toastController: ToastController,
        private router: Router
    ) {
        this.platform.ready().then(() => {
            KeepAwake.keepAwake().then(
                () => console.log('keepAwake success'),
                (e) => console.log('keepAwake error', e)
            );

            this.settingsService.loggedIn$
                .pipe(
                    filter((loggedIn) => loggedIn),
                    debounceTime(500),
                    withLatestFrom(this.supportService.unreadCount$)
                )
                .subscribe(async ([_, unreadCount]) => {
                    if (unreadCount > 0) {
                        const toast = await this.toastController.create({
                            message: `You have ${unreadCount} support tickets with new updates.`,
                            position: 'top',
                            color: 'warning',
                            layout: 'stacked',
                            buttons: [
                                {
                                    text: 'Go to support tickets',
                                    handler: () => {
                                        this.toastController.dismiss();
                                        this.router.navigateByUrl('/support');
                                    }
                                },
                                {
                                    text: 'Dismiss',
                                    handler: () => this.toastController.dismiss()
                                }
                            ]
                        });
                        toast.present();
                    }
                });
        });
    }
}
